// Tableau de bord — refonte transverse.
//
// La V1 ne parlait que de pointage: 4 KPI, les suivis du jour, les
// personnes signalées. Depuis, l'app a gagné congés, récup, contrats,
// documents, évaluations, recrutement, paie et l'espace employé — et
// rien de tout ça n'atterrissait ici. Deux blocs neufs répondent à ça:
//
//  · « Aujourd'hui » — l'état QUALIFIÉ de la journée. Un vendredi, un
//    jour férié ou une journée dont le pointage n'est pas encore importé
//    n'affichent plus « 0 présents »: le serveur dit pourquoi il n'y a
//    pas de chiffre, et on affiche la raison à la place du zéro.
//  · « À traiter » — l'inbox transverse: ce que chaque module attend de
//    vous, déjà filtré par vos permissions, avec le lien direct.
//
// Le reste (KPI, suivis, signalements, activité) est conservé: c'était
// la partie qui marchait.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  useMe, useEntities, useFollowups, useDashboardKpis, useDashboardFlagged,
  useDashboardOverview, useActivity, useFollowupDraft, useSendFollowupEmail,
  type AwayPersonDTO, type DashboardDayDTO, type FlaggedPersonDTO, type DashboardView,
  type TodoItemDTO, type PendingValidationDayDTO,
} from '../api/hooks.js';
import { activityLabel } from '@pointage/shared';
import { useUiStore } from '../store/ui.js';
import { todayYmd, addDaysToYmd, fmtFrLong } from '../lib/date.js';
import { DateNavigator } from '../components/DateNavigator.js';
import { toast } from '../store/toast.js';

// Monday (local) of the week containing a YYYY-MM-DD string.
function mondayOfYmd(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  const diff = (dt.getDay() + 6) % 7; // days since Monday
  return addDaysToYmd(ymd, -diff);
}

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const DAY_FMT = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const DOW_SHORT = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];

function weekNumber(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - day + 3);
  const firstThursday = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  const fday = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - fday + 3);
  return 1 + Math.round((t.getTime() - firstThursday.getTime()) / (7 * 864e5));
}

function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

// Build a sparkline path (60×22 viewBox) from up to 7 values.
function sparkPath(values: number[]): string {
  if (values.length === 0) return '';
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const stepX = 60 / Math.max(values.length - 1, 1);
  return values
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * stepX).toFixed(1)},${(20 - ((v - min) / span) * 16).toFixed(1)}`)
    .join(' ');
}

// ─── état de la journée ──────────────────────────────────────
// Le cœur du correctif: un compteur de présence n'a de sens QUE sur une
// journée ouvrée dont le pointage est arrivé. Partout ailleurs on nomme
// la raison plutôt que d'afficher un zéro qui ment.
interface DayFraming {
  chip: string;
  chipTone: string;
  headline: string;
  /** false → aucun compteur de présence n'est affiché. */
  countable: boolean;
  /** Un jour chômé (week-end, férié) ou à venir n'a pas non plus
   * d'absences justifiées à montrer: 0 n'y veut rien dire. Sur une
   * journée non importée, si: les décisions ne viennent pas de la
   * pointeuse. */
  justifieesMeaningful: boolean;
}
function frameDay(day: DashboardDayDTO | undefined): DayFraming {
  if (!day) return { chip: '…', chipTone: '', headline: '', countable: false, justifieesMeaningful: false };
  const c = day.counts;
  switch (day.state) {
    case 'weekend':
      return {
        chip: 'Week-end', chipTone: 'off', countable: false, justifieesMeaningful: false,
        headline: 'Aucun pointage attendu — vendredi et samedi sont chômés.',
      };
    case 'holiday':
      return {
        chip: day.holidayName ? `Férié · ${day.holidayName}` : 'Jour férié', chipTone: 'off',
        countable: false, justifieesMeaningful: false,
        headline: 'Jour férié — aucun pointage attendu.',
      };
    case 'future':
      return {
        chip: 'À venir', chipTone: 'off', countable: false, justifieesMeaningful: false,
        headline: `Journée à venir — ${c.roster} personne${c.roster > 1 ? 's' : ''} au registre.`,
      };
    case 'pending':
      return {
        chip: 'Journée en cours', chipTone: 'warn', countable: false, justifieesMeaningful: true,
        headline: `Le pointage du jour n’a pas encore été importé — ${c.roster} personne${c.roster > 1 ? 's' : ''} au registre, aucune présence connue pour l’instant.`,
      };
    case 'not-imported':
      return {
        chip: 'Pointage non importé', chipTone: 'bad', countable: false, justifieesMeaningful: true,
        headline: 'Le fichier de la pointeuse n’est jamais arrivé pour cette journée — présences et absences sont inconnues, pas nulles.',
      };
    default: {
      const rate = c.expected > 0 ? Math.round((c.present / c.expected) * 100) : 0;
      // Import partiel: les équipes sans fichier ne sont pas dans le
      // dénominateur, il faut le dire plutôt que laisser croire au total.
      const missing = day.missingEntities ?? [];
      const gap = missing.length > 0
        ? ` ${missing.map((e) => e.short).join(', ')} : pointage non importé, ces équipes ne sont pas comptées.`
        : '';
      return {
        chip: 'Journée ouvrée', chipTone: 'ok', countable: true, justifieesMeaningful: true,
        headline: `${c.present} présent${c.present > 1 ? 's' : ''} sur ${c.expected} attendu${c.expected > 1 ? 's' : ''} · ${rate} % de présence.${gap}`,
      };
    }
  }
}

const AWAY_LABEL: Record<AwayPersonDTO['kind'], { txt: string; cls: string }> = {
  CONGE: { txt: 'Congé', cls: 'conge' },
  RECUP: { txt: 'Récup', cls: 'recup' },
  MALADIE: { txt: 'Maladie', cls: 'maladie' },
  TT: { txt: 'Télétravail', cls: 'tt' },
  AJ: { txt: 'Absence justifiée', cls: 'justif' },
  ANJ: { txt: 'Non justifiée', cls: 'abs' },
};

export function DashboardPage() {
  const me = useMe();
  const navigate = useNavigate();
  const entityId = useUiStore((s) => s.currentEntityId);
  const [view, setView] = useState<DashboardView>('day');
  const [date, setDate] = useState(todayYmd());
  const today = new Date();
  const isWeek = view === 'week';

  const { data: entities } = useEntities();
  const colorByEntity = new Map((entities ?? []).map((e) => [e.id, e.color]));
  const entityNameById = new Map((entities ?? []).map((e) => [e.id, e.name]));
  const overview = useDashboardOverview({ entityId, date });
  const kpis = useDashboardKpis({ entityId, date, view });
  // Flagged stays a 7-day rolling window (per spec); followups stay
  // scoped to the selected day.
  const flagged = useDashboardFlagged({ entityId, date });
  const followups = useFollowups({ date, entityId, pollMs: 60_000 });
  const activity = useActivity(8);

  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  // « Préparer un message » n'était qu'un toast : aucun email, aucun
  // brouillon. Il ouvre maintenant un vrai modal de composition, et
  // seulement pour qui peut réellement envoyer.
  const [composeFor, setComposeFor] = useState<{ employeeId: string; name: string } | null>(null);
  const canSendEmail = (me.data?.permissions ?? []).includes('email.send');
  const weekStart = mondayOfYmd(date);
  const weekEnd = addDaysToYmd(weekStart, 6);
  const isToday = date === todayYmd();
  // L'entête suit la date CHOISIE: un panneau « Aujourd'hui » au-dessus
  // de chiffres du 27 juillet ne trompait personne longtemps.
  const [dy, dm, dd] = date.split('-').map(Number);
  const selected = new Date(dy!, (dm ?? 1) - 1, dd ?? 1);

  const firstName = (me.data?.user.name ?? '').split(/\s+/)[0] || '';
  const followCount = followups.data?.length ?? 0;
  const todo = overview.data?.todo ?? [];
  const todoCount = todo.reduce((a, t) => a + t.count, 0);
  const day = overview.data?.day;
  const framing = frameDay(day);

  // Le bonjour parle de la charge réelle du jour, pas du seul pointage.
  const load = todoCount + followCount;
  const mood =
    load === 0 ? 'rien n’attend de décision.'
      : load <= 3 ? 'tout est presque sous contrôle.'
        : load <= 8 ? 'quelques arbitrages à faire.'
          : 'il y a du travail aujourd’hui.';

  const flaggedPeople = (flagged.data?.people ?? []).filter((p) => !dismissed.has(p.employeeId + p.type));
  const away = overview.data?.away ?? [];
  const pendingDays = overview.data?.pendingValidation ?? [];

  return (
    <>
      <div className="page-header">
        <div className="page-eyebrow">
          {isWeek
            ? `semaine du ${fmtFrLong(weekStart)} au ${fmtFrLong(weekEnd)}`
            : `${DATE_FMT.format(selected)} — semaine ${weekNumber(selected)}${isToday ? '' : ' · date consultée'}`}
        </div>
        <h1 className="page-title">Bonjour {firstName}, <em>{mood}</em></h1>
        <p className="page-subtitle">
          {todoCount > 0
            ? `${todoCount} élément${todoCount > 1 ? 's' : ''} à traiter dans ${todo.length} module${todo.length > 1 ? 's' : ''}`
            : 'Aucune décision en attente'}
          {followCount > 0 ? ` · ${followCount} suivi${followCount > 1 ? 's' : ''} à envoyer` : ''}.
        </p>
      </div>

      {/* View toggle + date picker */}
      <div className="toolbar" style={{ marginBottom: 18 }} data-testid="dash-controls">
        <div className="segmented" role="tablist" aria-label="Vue">
          <button className={view === 'day' ? 'seg active' : 'seg'} onClick={() => setView('day')} data-testid="dash-view-day" role="tab" aria-selected={view === 'day'}>
            <span className="seg-dot" />Jour
          </button>
          <button className={view === 'week' ? 'seg active' : 'seg'} onClick={() => setView('week')} data-testid="dash-view-week" role="tab" aria-selected={view === 'week'}>
            <span className="seg-dot" />Semaine
          </button>
        </div>
        <div className="toolbar-spacer" />
        <DateNavigator value={date} onChange={setDate} />
      </div>

      {/* ── Aujourd'hui + À traiter ── */}
      <div className="dash-top">
        <TodayPanel
          day={day} framing={framing} away={away} colorByEntity={colorByEntity}
          loading={overview.isLoading} onGo={(href) => navigate(href)}
          title={isToday ? "Aujourd'hui" : DAY_FMT.format(selected)}
        />
        <TodoPanel todo={todo} loading={overview.isLoading} onGo={(href) => navigate(href)} />
      </div>

      {/* ── Journées non verrouillées ──
          L'état de validation ne vivait que dans Saisie, une entité et
          une quinzaine à la fois: treize journées ont dormi en brouillon
          deux semaines sans qu'un écran le dise. Ici, toutes entités. */}
      {pendingDays.length > 0 && (
        <PendingValidationPanel
          days={pendingDays}
          entityName={(id) => entityNameById.get(id) ?? id}
          colorByEntity={colorByEntity}
          onGo={(d) => navigate(`/entry?entity=${d.entityId}&date=${d.date}`)}
        />
      )}

      {/* KPIs */}
      <div className="kpis">
        <KpiCard
          tone="" label={isWeek ? 'Présents · semaine' : "Présents aujourd'hui"}
          value={!isWeek && !framing.countable ? '—' : kpis.data ? `${kpis.data.presents.value}` : '—'}
          suffix={!isWeek && !framing.countable ? '' : kpis.data ? `/${kpis.data.presents.total}` : ''}
          spark={kpis.data?.presents.sparkline ?? []} sparkColor="var(--ok)"
          foot={
            !isWeek && !framing.countable
              ? framing.chip
              : kpis.data ? `${Math.round((kpis.data.presents.value / Math.max(kpis.data.presents.total, 1)) * 100)} % de présence` : ''
          }
          delta={kpis.data?.presents.delta ?? 0} deltaSuffix={isWeek ? 'sem. préc.' : 'hier'}
          mute={!isWeek && !framing.countable} testid="kpi-presents"
        />
        <KpiCard
          tone="kpi-warn" label="Retards"
          value={!isWeek && !framing.countable ? '—' : kpis.data ? `${kpis.data.retards.value}` : '—'}
          spark={kpis.data?.retards.sparkline ?? []} sparkColor="var(--warn)"
          foot={!isWeek && !framing.countable ? framing.chip : isWeek ? 'cette semaine' : 'cette journée'}
          delta={kpis.data?.retards.delta ?? 0} deltaSuffix={isWeek ? 'sem. préc.' : 'hier'}
          mute={!isWeek && !framing.countable} testid="kpi-retards"
        />
        <KpiCard
          tone="kpi-info" label="Absences justifiées"
          value={!isWeek && !framing.justifieesMeaningful ? '—' : kpis.data ? `${kpis.data.absencesJustifiees.value}` : '—'}
          spark={kpis.data?.absencesJustifiees.sparkline ?? []} sparkColor="var(--info)"
          foot={
            !isWeek && !framing.justifieesMeaningful
              ? framing.chip
              : kpis.data ? `${kpis.data.absencesJustifiees.breakdown.conge} congé · ${kpis.data.absencesJustifiees.breakdown.maladie} maladie` : ''
          }
          delta={kpis.data?.absencesJustifiees.delta ?? 0} deltaSuffix={isWeek ? 'sem. préc.' : 'hier'}
          mute={!isWeek && !framing.justifieesMeaningful} testid="kpi-justifiees"
        />
        <KpiCard
          tone="kpi-bad" label="Non justifiées"
          value={!isWeek && !framing.countable ? '—' : kpis.data ? `${kpis.data.absencesNonJustifiees.value}` : '—'}
          spark={kpis.data?.absencesNonJustifiees.sparkline ?? []} sparkColor="var(--bad)"
          foot={!isWeek && !framing.countable ? framing.chip : 'action requise'}
          delta={kpis.data?.absencesNonJustifiees.delta ?? 0} deltaSuffix={isWeek ? 'sem. préc.' : 'hier'}
          mute={!isWeek && !framing.countable} testid="kpi-nonjustifiees"
        />
      </div>

      {/* Suivis + week chart */}
      <div className="dash-grid">
        <div className="card" data-testid="dash-suivis">
          <div className="card-header">
            <div className="card-title">Suivis à envoyer <span className="card-title-count">{followCount} brouillon{followCount > 1 ? 's' : ''}</span></div>
          </div>
          {followCount === 0 && <div style={{ padding: '18px 20px', color: 'var(--ink-3)', fontSize: 13 }}>Aucun suivi à envoyer aujourd'hui.</div>}
          {(followups.data ?? []).slice(0, 5).map((f, i) => (
            <div className="followup" key={`${f.employee.id}-${i}`}>
              <div className="followup-avatar" style={{ background: colorByEntity.get(f.employee.entityId) ?? 'var(--shf)' }}>{initials(f.employee.name)}</div>
              <div className="followup-main">
                <div className="followup-name">{f.employee.name}<span className="followup-entity">{f.employee.entityId}</span></div>
                <div className="followup-detail"><strong>{f.label}</strong> · {f.detail}</div>
              </div>
              <div className="followup-action" style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm" onClick={() => navigate('/entry')}>Examiner →</button>
                {/* La carte annonce des « brouillons » depuis toujours sans
                    jamais pouvoir en envoyer un — le modal comble ça. */}
                {canSendEmail && (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => setComposeFor({ employeeId: f.employee.id, name: f.employee.name })}
                    data-testid={`followup-compose-${f.employee.id}`}
                  >
                    Message
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card-header"><div className="card-title">{isWeek ? 'Aperçu de la semaine sélectionnée' : 'Aperçu des 7 derniers jours'}</div></div>
          <WeekChart kpis={kpis.data} view={view} refYmd={date} />
        </div>
      </div>

      {/* Personnes signalées */}
      <div className="card" style={{ marginBottom: 18 }} data-testid="dash-flagged">
        <div className="card-header">
          <div className="card-title">Personnes signalées <span className="card-title-count">{flaggedPeople.length} cette semaine</span></div>
          <span className="card-action" style={{ color: 'var(--ink-3)', fontSize: 11.5 }}>Signalements automatiques · 1 semaine glissante</span>
        </div>
        {flaggedPeople.length === 0 && <div style={{ padding: '18px 20px', color: 'var(--ink-3)', fontSize: 13 }}>Aucune personne signalée cette semaine.</div>}
        {flaggedPeople.map((p) => (
          <FlagRow
            key={p.employeeId + p.type}
            person={p}
            color={colorByEntity.get(p.entityId) ?? 'var(--warn)'}
            onIgnore={() => setDismissed((s) => new Set(s).add(p.employeeId + p.type))}
            onCompose={canSendEmail ? () => setComposeFor({ employeeId: p.employeeId, name: p.name }) : undefined}
          />
        ))}
      </div>

      {composeFor && (
        <ComposeFollowupModal
          person={composeFor}
          date={date}
          onClose={() => setComposeFor(null)}
        />
      )}

      {/* Activity */}
      <div className="card">
        <div className="card-header"><div className="card-title">Activité récente <span className="card-title-count">récent</span></div></div>
        <div className="activity">
          {(activity.data ?? []).map((a) => (
            <div className="activity-row" key={a.id}>
              <div className="activity-time">{a.createdAt.slice(11, 16)}</div>
              <div className="activity-icon" />
              <div className="activity-text">{activityLabel(a)}{a.user ? <> — <strong>{a.user.name}</strong></> : null}</div>
              <div className="activity-user">{a.createdAt.slice(0, 10)}</div>
            </div>
          ))}
          {(activity.data?.length ?? 0) === 0 && <div style={{ padding: '12px 20px', color: 'var(--ink-3)', fontSize: 13 }}>Aucune activité récente.</div>}
        </div>
      </div>
    </>
  );
}


// ─── « Préparer un message » ─────────────────────────────────
// Le bouton n'était qu'un toast: aucun email, aucun brouillon. Il ouvre
// maintenant le vrai circuit — POST /api/followups/email, le même que
// les relances — avec sujet et corps pré-remplis par les gabarits FR du
// serveur et éditables avant envoi. Les gabarits restent serveur: un
// message parti au nom de quelqu'un se rédige à partir des faits que le
// serveur a, pas d'un texte réinventé côté client.
function ComposeFollowupModal({ person, date, onClose }: {
  // Les deux cartes qui ouvrent ce modal ne portent pas le même DTO
  // (personne signalée / suivi du jour) — seuls l'id et le nom comptent.
  person: { employeeId: string; name: string };
  date: string;
  onClose: () => void;
}) {
  const draft = useFollowupDraft({ employeeId: person.employeeId, date });
  const send = useSendFollowupEmail();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [touched, setTouched] = useState(false);

  // Hydrate once: une frappe en cours ne doit pas être écrasée par un
  // refetch.
  useEffect(() => {
    if (touched || !draft.data) return;
    setSubject(draft.data.subject);
    setBody(draft.data.body);
  }, [draft.data, touched]);

  const d = draft.data;
  const noEmail = d != null && !d.employee.email;
  // Le serveur ne refuse (409) que si TOUS les motifs ont déjà été
  // envoyés pour cette journée; sinon il envoie les nouveaux et saute
  // les doublons. Le bouton dit la même chose que la route.
  const sentKinds = new Set(d?.alreadySent ?? []);
  const allSent = d != null && d.kinds.length > 0 && d.kinds.every((k) => sentKinds.has(k));
  const partlySent = sentKinds.size > 0 && !allSent;
  const err = draft.error as (Error & { body?: { message?: string } }) | null;

  const onSend = async () => {
    if (!d) return;
    try {
      await send.mutateAsync({
        employeeId: person.employeeId,
        triggerDate: d.triggerDate,
        kinds: d.kinds,
        subject,
        body,
      });
      toast.success(`Message envoyé à ${person.name}.`);
      onClose();
    } catch (e) {
      const x = e as Error & { status?: number; body?: { message?: string } };
      toast.danger(
        x.status === 409
          ? `Un message a déjà été envoyé à ${person.name} pour cette journée.`
          : x.body?.message ?? "Échec de l'envoi du message.",
      );
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-card modal-card--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="compose-title"
        onClick={(e) => e.stopPropagation()}
        data-testid="compose-followup"
      >
        <h2 id="compose-title">Message à {person.name}</h2>

        {draft.isLoading && <p className="page-placeholder">Préparation du brouillon…</p>}

        {draft.isError && (
          <>
            <p className="page-placeholder" data-testid="compose-error">
              {err?.body?.message ?? 'Impossible de préparer un brouillon.'}
            </p>
            <div className="modal-actions">
              <button type="button" className="btn" onClick={onClose}>Fermer</button>
            </div>
          </>
        )}

        {d && (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              Envoyé à <strong>{d.employee.email ?? '— aucun email enregistré'}</strong> · journée
              de référence <strong>{d.triggerDate}</strong>
            </p>
            <ul className="muted" style={{ marginTop: 0 }}>
              {d.items.map((it) => (
                <li key={it.kind}><strong>{it.label}</strong> — {it.detail}</li>
              ))}
            </ul>
            {(allSent || partlySent) && (
              <p style={{ color: 'var(--warn)', fontSize: 13 }} data-testid="compose-already-sent">
                {allSent
                  ? 'Un message a déjà été envoyé pour cette journée — un second sera refusé.'
                  : `Déjà envoyé pour : ${[...sentKinds].join(', ')} — seuls les autres motifs partiront.`}
              </p>
            )}
            <div className="f">
              <label className="f-l" htmlFor="compose-subject">Objet</label>
              <input
                id="compose-subject"
                className="compose-field"
                value={subject}
                onChange={(e) => { setTouched(true); setSubject(e.target.value); }}
                data-testid="compose-subject"
              />
            </div>
            <div className="f">
              <label className="f-l" htmlFor="compose-body">Message</label>
              <textarea
                id="compose-body"
                className="compose-field"
                value={body}
                onChange={(e) => { setTouched(true); setBody(e.target.value); }}
                data-testid="compose-body"
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn" onClick={onClose} data-testid="compose-cancel">
                Annuler
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={onSend}
                disabled={send.isPending || noEmail || allSent || !subject.trim() || !body.trim()}
                title={noEmail ? 'Aucun email enregistré pour ce salarié.' : undefined}
                data-testid="compose-send"
              >
                {send.isPending ? 'Envoi…' : 'Envoyer le message'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── « Aujourd'hui » ─────────────────────────────────────────
const AWAY_VISIBLE = 12; // au-delà, la liste devient un mur — on résume.

function TodayPanel({ day, framing, away, colorByEntity, loading, onGo, title }: {
  day: DashboardDayDTO | undefined;
  framing: DayFraming;
  away: AwayPersonDTO[];
  colorByEntity: Map<string, string>;
  loading: boolean;
  onGo: (href: string) => void;
  title: string;
}) {
  const [showAll, setShowAll] = useState(false);
  const c = day?.counts;
  const shownAway = showAll ? away : away.slice(0, AWAY_VISIBLE);
  // Un jour chômé ne « cache » pas les congés posés dessus: on garde les
  // motifs d'absence, on retire seulement les compteurs de présence.
  const tiles: Array<{ k: string; label: string; value: number; cls: string }> = [];
  if (c) {
    if (framing.countable) {
      tiles.push({ k: 'present', label: 'Présents', value: c.present, cls: 'ok' });
      tiles.push({ k: 'retard', label: 'Retards', value: c.retard, cls: 'warn' });
    }
    if (c.tt > 0) tiles.push({ k: 'tt', label: 'Télétravail', value: c.tt, cls: 'tt' });
    if (c.conge > 0) tiles.push({ k: 'conge', label: 'En congé', value: c.conge, cls: 'conge' });
    if (c.recup > 0) tiles.push({ k: 'recup', label: 'Récup', value: c.recup, cls: 'recup' });
    if (c.maladie > 0) tiles.push({ k: 'maladie', label: 'Maladie', value: c.maladie, cls: 'info' });
    if (framing.countable && c.absent > 0) tiles.push({ k: 'absent', label: 'Non justifiées', value: c.absent, cls: 'bad' });
  }

  return (
    <div className="card dash-today" data-testid="dash-today">
      <div className="card-header">
        <div className="card-title">
          {title}
          <span className={'day-chip ' + framing.chipTone} data-testid="dash-day-chip">{framing.chip}</span>
        </div>
        {c && <span className="card-action" style={{ color: 'var(--ink-3)', fontSize: 11.5 }}>{c.roster} au registre</span>}
      </div>
      <div className="dash-today-body">
        {loading && <p className="dash-muted">Chargement…</p>}
        {!loading && <p className="dash-headline" data-testid="dash-day-headline">{framing.headline}</p>}
        {tiles.length > 0 && (
          <div className="day-tiles">
            {tiles.map((t) => (
              <div className={'day-tile ' + t.cls} key={t.k}>
                <span className="day-tile-v">{t.value}</span>
                <span className="day-tile-l">{t.label}</span>
              </div>
            ))}
          </div>
        )}
        {away.length > 0 && (
          <>
            <div className="dash-subhead">Qui n'est pas au bureau <span className="dash-subhead-n">{away.length}</span></div>
            <div className="away-list">
              {shownAway.map((a) => {
                const L = AWAY_LABEL[a.kind];
                return (
                  <span className={'away-chip ' + L.cls} key={a.employeeId + a.kind} title={a.until ? `jusqu'au ${a.until}` : L.txt}>
                    <span className="away-av" style={{ background: colorByEntity.get(a.entityId) ?? 'var(--ink-3)' }}>{initials(a.name)}</span>
                    <span className="away-nm">{a.name.split(/\s+/)[0]}</span>
                    <span className="away-kind">{L.txt}{a.until ? ` · au ${Number(a.until.slice(8, 10))}` : ''}</span>
                  </span>
                );
              })}
              {!showAll && away.length > AWAY_VISIBLE && (
                <button className="away-more" onClick={() => setShowAll(true)}>
                  +{away.length - AWAY_VISIBLE} autres
                </button>
              )}
            </div>
          </>
        )}
        {!loading && away.length === 0 && framing.countable && (
          <p className="dash-muted">Tout le monde est là.</p>
        )}
        {!loading && (day?.state === 'pending' || day?.state === 'not-imported') && (
          <button className="btn btn-sm btn-primary dash-cta" onClick={() => onGo('/entry')} data-testid="dash-import-cta">
            Importer le pointage →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── « À traiter » — l'inbox transverse ──────────────────────
function TodoPanel({ todo, loading, onGo }: { todo: TodoItemDTO[]; loading: boolean; onGo: (href: string) => void }) {
  return (
    <div className="card dash-todo" data-testid="dash-todo">
      <div className="card-header">
        <div className="card-title">
          À traiter
          <span className="card-title-count">{todo.length} module{todo.length > 1 ? 's' : ''}</span>
        </div>
      </div>
      {loading && <div className="dash-todo-empty">Chargement…</div>}
      {!loading && todo.length === 0 && (
        <div className="dash-todo-empty">✓ Rien n'attend de décision — congés, contrats, évaluations et recrutement sont à jour.</div>
      )}
      {todo.map((t) => (
        <button className={'todo-row t-' + t.tone} key={t.key} onClick={() => onGo(t.href)} data-testid={`todo-${t.key}`}>
          <span className="todo-count">{t.count}</span>
          <span className="todo-main">
            <span className="todo-label">{t.label}</span>
            <span className="todo-detail">{t.detail}</span>
          </span>
          <span className="todo-go">→</span>
        </button>
      ))}
    </div>
  );
}

// ─── « Journées non verrouillées » ───────────────────────────
// Une journée dont le pointage est arrivé mais qui n'est pas validée: le
// récapitulatif ne part pas et la paie travaille sur du brouillon. Deux
// cas à ne pas confondre — « prête à valider » (un clic) et « N décisions
// à prendre » (il reste du travail d'arbitrage).
function PendingValidationPanel({ days, entityName, colorByEntity, onGo }: {
  days: PendingValidationDayDTO[];
  entityName: (id: string) => string;
  colorByEntity: Map<string, string>;
  onGo: (d: PendingValidationDayDTO) => void;
}) {
  const ready = days.filter((d) => d.blockers === 0).length;
  const oldest = days[0]!.date; // trié plus ancien d'abord côté serveur
  return (
    <div className="card dash-pending" data-testid="dash-pending">
      <div className="card-header">
        <div className="card-title">
          Journées non verrouillées
          <span className="card-title-count">{days.length} journée{days.length > 1 ? 's' : ''}</span>
        </div>
        <div className="card-sub">
          la plus ancienne remonte au {fmtFrLong(oldest)}
          {ready > 0 ? ` · ${ready} prête${ready > 1 ? 's' : ''} à valider` : ''}
        </div>
      </div>
      <div className="dash-pending-list">
        {days.slice(0, 12).map((d) => (
          <button
            className={'pending-row' + (d.blockers === 0 ? ' ready' : '')}
            key={`${d.entityId}|${d.date}`}
            onClick={() => onGo(d)}
            data-testid={`pending-${d.entityId}-${d.date}`}
          >
            <span className="pending-dot" style={{ background: colorByEntity.get(d.entityId) ?? 'var(--shf)' }} />
            <span className="pending-main">
              <span className="pending-label">{fmtFrLong(d.date)}</span>
              <span className="pending-detail">
                {entityName(d.entityId)} · {d.records} ligne{d.records > 1 ? 's' : ''}
              </span>
            </span>
            <span className={'pending-state' + (d.blockers === 0 ? ' ok' : '')}>
              {d.blockers === 0
                ? 'prête à valider'
                : `${d.blockers} décision${d.blockers > 1 ? 's' : ''} à prendre`}
            </span>
            <span className="todo-go">→</span>
          </button>
        ))}
        {days.length > 12 && (
          <div className="dash-pending-more">+{days.length - 12} autres journées — ouvrez Saisie quotidienne.</div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ tone, label, value, suffix, spark, sparkColor, foot, delta, deltaSuffix = 'hier', mute, testid }: {
  tone: string; label: string; value: string; suffix?: string; spark: number[]; sparkColor: string; foot: string;
  delta: number; deltaSuffix?: string; mute?: boolean; testid: string;
}) {
  const deltaCls = delta > 0 ? 'up' : delta < 0 ? 'down' : 'neutral';
  const deltaTxt = delta === 0 ? `= ${deltaSuffix}` : `${delta > 0 ? '↑' : '↓'} ${Math.abs(delta)} vs ${deltaSuffix}`;
  return (
    <div className={`kpi ${tone}${mute ? ' kpi-mute' : ''}`.trim()} data-testid={testid}>
      <div className="kpi-label"><span className="kpi-label-dot" />{label}</div>
      <div className="kpi-value">{value}{suffix && <span className="kpi-suffix">{suffix}</span>}</div>
      {!mute && <svg className="kpi-spark" viewBox="0 0 60 22"><path d={sparkPath(spark)} fill="none" stroke={sparkColor} strokeWidth="1.5" strokeLinecap="round" /></svg>}
      <div className="kpi-foot"><span>{foot}</span>{!mute && <span className={`kpi-delta ${deltaCls}`}>{deltaTxt}</span>}</div>
    </div>
  );
}

function WeekChart({ kpis, view, refYmd }: { kpis: ReturnType<typeof useDashboardKpis>['data']; view: DashboardView; refYmd: string }) {
  const present = kpis?.presents.sparkline ?? [0, 0, 0, 0, 0, 0, 0];
  const retard = kpis?.retards.sparkline ?? [0, 0, 0, 0, 0, 0, 0];
  const justif = kpis?.absencesJustifiees.sparkline ?? [0, 0, 0, 0, 0, 0, 0];
  const anj = kpis?.absencesNonJustifiees.sparkline ?? [0, 0, 0, 0, 0, 0, 0];
  const totals = present.map((p, i) => p + justif[i]! + anj[i]!);
  const max = Math.max(...totals, 1);
  const H = 120;
  // Day view: trailing 7 days ending refYmd. Week view: Mon→Sun of refYmd's week.
  const startYmd = view === 'week' ? mondayOfYmd(refYmd) : addDaysToYmd(refYmd, -6);
  const todayStr = todayYmd();
  const days = Array.from({ length: 7 }, (_, i) => {
    const ymd = addDaysToYmd(startYmd, i);
    const [y, m, d] = ymd.split('-').map(Number);
    return { ymd, date: new Date(y!, (m ?? 1) - 1, d ?? 1) };
  });
  return (
    <div className="weekchart">
      <div className="weekchart-bars">
        {present.map((p, i) => {
          const onTime = Math.max(0, p - retard[i]!);
          const seg = (v: number) => `${(v / max) * H}px`;
          const isToday = days[i]?.ymd === todayStr;
          return (
            <div className={`weekbar${isToday ? ' today' : ''}`} key={i}>
              <div className="weekbar-stack">
                <div className="weekbar-seg ok" style={{ height: seg(onTime) }} />
                <div className="weekbar-seg warn" style={{ height: seg(retard[i]!) }} />
                <div className="weekbar-seg info" style={{ height: seg(justif[i]!) }} />
                <div className="weekbar-seg bad" style={{ height: seg(anj[i]!) }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="weekchart-labels">
        {days.map((d, i) => (
          <div className={`weekchart-label${d.ymd === todayStr ? ' today' : ''}`} key={i}>
            <span className="d">{d.date.getDate()}</span><span className="l">{DOW_SHORT[d.date.getDay()]}</span>
          </div>
        ))}
      </div>
      <div className="weekchart-legend">
        <span className="weekchart-legend-item"><span className="weekchart-legend-dot" style={{ background: 'var(--ok)' }} />Présents</span>
        <span className="weekchart-legend-item"><span className="weekchart-legend-dot" style={{ background: 'var(--warn)' }} />Retards</span>
        <span className="weekchart-legend-item"><span className="weekchart-legend-dot" style={{ background: 'var(--info)' }} />Justifiés</span>
        <span className="weekchart-legend-item"><span className="weekchart-legend-dot" style={{ background: 'var(--bad)' }} />Non justifiés</span>
      </div>
    </div>
  );
}

const FLAG_TAG: Record<FlaggedPersonDTO['type'], { cls: string; label: (c: number) => string }> = {
  retards: { cls: 'late', label: (c) => `${c} retards / semaine` },
  'sous-pointage': { cls: 'less', label: (c) => `${c} journées < 8h` },
  absences: { cls: 'abs', label: () => 'Absences répétées' },
};
const PATTERN_CLS: Record<string, string> = { ok: 'ok', late: 'late', short: 'late', absent: 'bad', off: '', none: '' };

function FlagRow({ person, color, onIgnore, onCompose }: {
  person: FlaggedPersonDTO;
  color: string;
  onIgnore: () => void;
  /** Absent quand le compte n'a pas email.send — pas de bouton mort. */
  onCompose?: () => void;
}) {
  const tag = FLAG_TAG[person.type];
  return (
    <div className="flag-row" data-testid={`flag-${person.employeeId}`}>
      <div className="followup-avatar" style={{ background: color }}>{initials(person.name)}</div>
      <div className="flag-row-main">
        <div className="flag-row-name">{person.name}<span className="followup-entity">{person.entityShort}</span><span className={`tag ${tag.cls}`} style={{ marginLeft: 4 }}>{tag.label(person.count)}</span></div>
        <div className="flag-row-detail">{person.impact}</div>
        <div className="flag-bar">
          {person.pattern.map((c, i) => <span key={i} className={`flag-bar-cell ${PATTERN_CLS[c] ?? ''}`} />)}
        </div>
      </div>
      <div className="flag-row-actions" style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button className="btn btn-sm btn-ghost" onClick={onIgnore} data-testid={`flag-ignore-${person.employeeId}`}>Ignorer</button>
        {onCompose && (
          <button
            className="btn btn-sm btn-primary"
            onClick={onCompose}
            data-testid={`flag-compose-${person.employeeId}`}
          >
            Préparer un message
          </button>
        )}
      </div>
    </div>
  );
}
